/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { ChatHeader as CdsLabsChatHeader } from '@coursera-web/cds-labs';

import { useRetracked } from 'js/lib/retracked';

import { useTracker } from '@coursera/event-pulse/react';

import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

export type ChatPopupButton = 'in' | 'out' | undefined;

export type Props = {
  onClose: () => void;
  showClose: boolean;
  showStartNewChat?: boolean;
  showSettings?: boolean;
  handleNewSession?: () => void;
  handleSettingsClick?: () => void;
  loading?: boolean;
  isCoachStudio?: boolean;
  showFullScreenButton?: boolean;
  toggleFullScreen?: () => void;
  isFullScreen?: boolean;
  showPositionToggle?: boolean;
  handlePositionToggleClick?: () => void;
  chatPopupButton?: ChatPopupButton;
  handlePopupButtonClick?: () => void;
  showSearchButton?: boolean;
  searchButtonRef?: React.RefObject<HTMLButtonElement>;
  settingsButtonRef?: React.RefObject<HTMLButtonElement>;
  enableContinuousChat?: boolean;
  notifications?: React.ReactNode;
};

/**
 * Default header component for Coach chat pattern
 */
const ChatHeader = (props: Props) => {
  const {
    onClose,
    handleSettingsClick,
    showSettings,
    showClose,
    showStartNewChat = true,
    handleNewSession,
    loading = false,
    showFullScreenButton = false,
    toggleFullScreen,
    isFullScreen = false,
    showPositionToggle = false,
    handlePositionToggleClick,
    chatPopupButton,
    handlePopupButtonClick,
    showSearchButton = false,
    searchButtonRef,
    settingsButtonRef,
    enableContinuousChat = false,
    notifications,
  } = props;
  const trackRetracked = useRetracked();
  const trackingData = useCoachTrackingContext()?.data;
  const track = useTracker();

  const { enterSearchMode } = useCoachChatContext();

  const handleCoachSettingsClick = () => {
    handleSettingsClick?.();

    track('interact_coach', {
      coachInteractionType: 'coach_settings_open',
      coach: {
        mode: trackingData?.mode,
      },
    });

    // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
    track('click_coach_element', {
      clickElementType: 'open_settings',
      coach: {
        mode: trackingData?.mode,
      },
    });
  };

  const handleNewSessionClick = () => {
    if (!enableContinuousChat) {
      // V2 eventing
      trackRetracked({
        trackingData: {
          timestamp: Date.now(),
        },
        trackingName: 'ai_course_coach_chat_header_new_session_button',
        action: `click`,
      });

      // V3 eventing
      track('interact_coach', {
        coachInteractionType: 'start_new_chat_session',
        coach: {
          mode: trackingData?.mode,
        },
        ...(trackingData?.courseId && {
          product: {
            type: 'course',
            id: trackingData.courseId as string,
          },
        }),
        ...(trackingData?.itemId && {
          courseItem: {
            id: trackingData.itemId as string,
          },
        }),
      });

      // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
      track('click_coach_element', {
        clickElementType: 'start_new_chat',
        coach: {
          mode: trackingData?.mode,
        },
        ...(trackingData?.courseId && {
          product: {
            type: 'course',
            id: trackingData.courseId as string,
          },
        }),
        ...(trackingData?.itemId && {
          courseItem: {
            id: trackingData.itemId as string,
          },
        }),
      });
    }

    handleNewSession?.();
  };

  const handleCloseClick = () => {
    trackRetracked({
      trackingData: { timestamp: Date.now() },
      trackingName: 'ai_course_coach_chat_header_close_button',
      action: 'click',
    });
    onClose();
  };

  return (
    <CdsLabsChatHeader
      onSwapPosition={showPositionToggle ? handlePositionToggleClick : undefined}
      popupButtonMode={chatPopupButton}
      onPopupButtonClick={chatPopupButton ? handlePopupButtonClick : undefined}
      onSearch={showSearchButton ? () => enterSearchMode() : undefined}
      searchButtonRef={searchButtonRef}
      onNewChat={showStartNewChat ? handleNewSessionClick : undefined}
      onSettings={showSettings ? handleCoachSettingsClick : undefined}
      settingsButtonRef={settingsButtonRef}
      onToggleFullScreen={showFullScreenButton ? toggleFullScreen : undefined}
      isFullScreen={isFullScreen}
      onClose={showClose ? handleCloseClick : undefined}
      loading={loading}
      showFade
      notifications={notifications}
    />
  );
};

export default ChatHeader;
